/**
 * What the engine did, for a debugger to watch.
 *
 * Every rule in the platform is compiled and evaluated through `cache.ts`, so
 * that is the one place that can say what actually ran. While a listener is
 * installed it reports each compile and each evaluation: the source, how long
 * it took, whether the compiled form was already held, and what came back.
 *
 * WHY THIS EXISTS. `@crown/schema-core`'s rule tracer reports the RULES a form
 * checked - which fired, against what value. That leaves the layer underneath
 * dark: whether an expression compiled at all, whether it is being recompiled
 * on every keystroke, which one is slow, and what a failure said before it was
 * turned into "the rule did not pass". A field that goes blank tells you
 * nothing about which of those it was.
 *
 * Zero cost when nobody is listening: the emit is a null check, and no event is
 * built until there is somewhere for it to go.
 */

/** What the engine was asked to do. */
export type EngineEventKind = 'compile' | 'evaluate' | 'program';

/**
 * What an expression could read when it ran.
 *
 * The one thing a log of evaluations cannot answer on its own: an expression
 * that came out `false` says nothing about WHY until you can see what it was
 * looking at. These are the three the engine binds - the field's own value,
 * the document it sits in, and the caller's context bag.
 *
 * Captured only while `setEngineScopeCapture(true)`, because holding the whole
 * document per evaluation is the sort of thing that changes what it measures:
 * a form evaluating four hundred rules a keystroke would retain four hundred
 * references to state that would otherwise be collected.
 */
export interface EngineScope {
  /** The value the expression is about — `value`, `$`. */
  value: unknown;
  /** The document it belongs to — `root`. */
  root: unknown;
  /** Whatever the caller passed alongside — `ctx`. */
  bag: unknown;
}

/** One thing the engine did. */
export interface EngineEvent {
  /** Which of the three: a compile, an expression evaluation, or a program run. */
  kind: EngineEventKind;
  /** The source, as the caller wrote it. */
  source: string;
  /** How long it took, in milliseconds. */
  ms: number;
  /**
   * Whether a compiled form was already held for this source.
   *
   * A rule re-evaluated on every keystroke should be a hit every time after the
   * first; a miss per keystroke means the source is being rebuilt - a template
   * interpolated into the expression, say - and the cache is holding a new
   * entry for each.
   */
  cached?: boolean;
  /** What it came to, for an evaluation. Absent for a compile. */
  value?: unknown;
  /** What went wrong, if anything. */
  error?: string;
  /** What it could read, when scope capture is on. */
  scope?: EngineScope;
  /** Whether a breakpoint matched this source. */
  broke?: boolean;
  /** When it happened. */
  ts: number;
}

type EngineDebugListener = (event: EngineEvent) => void;

let listener: EngineDebugListener | null = null;

/**
 * Install (or remove) an engine listener. Pass `null` to stop.
 *
 * One at a time, deliberately: this is a debugging seam rather than an event
 * bus, and a second listener would mean two things quietly instrumenting the
 * same engine.
 */
export function setEngineDebugListener(fn: EngineDebugListener | null): void {
  listener = fn;
}

/** Whether anything is listening — check before building an event. */
export function isEngineDebugActive(): boolean {
  return listener !== null;
}

let captureScope = false;

/**
 * Record what each expression could read.
 *
 * Off by default and separate from the listener, because it is the expensive
 * half: the listener is a function call, this retains the document for every
 * evaluation it reports.
 */
export function setEngineScopeCapture(on: boolean): void {
  captureScope = on;
}

/** Whether scope is being captured. */
export function isEngineScopeCaptured(): boolean {
  return captureScope;
}

/** Decides whether the engine should stop on a given source. */
export type EngineBreakpoint = (source: string) => boolean;

let breakpoint: EngineBreakpoint | null = null;

/**
 * Called the moment a matching expression is about to run.
 *
 * THIS IS THE PLACE TO PUT A BREAKPOINT. Open the browser's debugger, find
 * `engineBreakpointHit`, and set one on it once; from then on
 * `setEngineBreakpoint` decides WHICH expression stops there. The pause lands
 * in the engine's real call stack, with the source and the scope in the frame
 * and every caller that led to it above - a stepper written here could not
 * offer that, and would only be a worse copy of the tool already open.
 *
 * Deliberately not a `debugger` statement: that halts a page in front of a
 * user the moment anyone has developer tools open, and it ships to production
 * in the bundle. A named function is inert until somebody chooses to stop on
 * it.
 *
 * Kept side-effect free and exported so a bundler cannot inline it away.
 */
export function engineBreakpointHit(
  source: string,
  scope: EngineScope | undefined,
): void {
  void source;
  void scope;
}

/**
 * Choose which expressions stop at `engineBreakpointHit`.
 *
 * Pass `null` to stop on none.
 */
export function setEngineBreakpoint(match: EngineBreakpoint | null): void {
  breakpoint = match;
}

/** Whether this source is one the caller asked to stop on. */
export function shouldBreakOn(source: string): boolean {
  if (!breakpoint) return false;
  try {
    return breakpoint(source);
  } catch {
    // A matcher that throws must not take the evaluation down with it.
    return false;
  }
}

/** Report one thing the engine did. Does nothing when nobody is listening. */
export function emitEngineEvent(event: EngineEvent): void {
  listener?.(event);
}

/**
 * Time `work`, reporting it as an engine event.
 *
 * Returns what `work` returned, and rethrows what it threw after reporting it:
 * a failure is the most interesting thing a debugger can be told about, and
 * swallowing it here would change what the caller sees.
 */
export function traced<T>(
  kind: EngineEventKind,
  source: string,
  cached: boolean,
  work: () => T,
  /**
   * What the expression can read, built only if it is going to be recorded.
   * A thunk, so an evaluation nobody is watching costs nothing to describe.
   */
  scope?: () => EngineScope,
): T {
  if (!isEngineDebugActive()) return work();
  const captured = captureScope && scope ? scope() : undefined;
  const broke = shouldBreakOn(source);
  // A breakpoint the caller asked for: stopping here is the reader's choice,
  // made by putting a browser breakpoint on `engineBreakpointHit`.
  if (broke) engineBreakpointHit(source, captured);
  const started = performance.now();
  try {
    const value = work();
    emitEngineEvent({
      kind,
      source,
      ms: performance.now() - started,
      cached,
      value,
      scope: captured,
      broke,
      ts: Date.now(),
    });
    return value;
  } catch (error) {
    emitEngineEvent({
      kind,
      source,
      ms: performance.now() - started,
      cached,
      error: error instanceof Error ? error.message : String(error),
      scope: captured,
      broke,
      ts: Date.now(),
    });
    throw error;
  }
}
